import { useState } from "react";
import type { ReactNode } from "react";
import { renderRichText } from "./RichProse";

export interface FAQItem {
  q: string;
  a: string;
  answerContent?: ReactNode;
  /** Answer with [label](/path) links; `a` stays the plain-text schema answer. */
  aMd?: string;
}

export function FAQ({ items, native = false, initiallyOpen = true }: { items: FAQItem[]; native?: boolean; initiallyOpen?: boolean }) {
  const [open, setOpen] = useState<number | null>(0);
  if (native) return (
    <div className="native-faq">
      {items.map((item, index) => (
        <details key={item.q} open={initiallyOpen && index === 0} className="border-t border-line-strong">
          <summary className="flex cursor-pointer items-center justify-between gap-6 py-[26px] text-bleu">
            <h3 className="m-0 font-sans text-[24px] font-light leading-[1.3] max-sm:text-[18px]">{item.q}</h3>
            <span aria-hidden="true" className="faq-plus mono shrink-0 text-[18px] text-champagne transition-transform">+</span>
          </summary>
          <div className="max-w-[68ch] pb-7 text-[15.5px] leading-[1.6] text-bleu-dim">{item.answerContent ?? (item.aMd ? renderRichText(item.aMd) : item.a)}</div>
        </details>
      ))}
    </div>
  );
  return (
    <ul className="m-0 list-none p-0">
      {items.map((it, i) => {
        const isOpen = open === i;
        return (
          <li
            key={i}
            style={{ borderTop: "1px solid rgba(0,47,167,0.26)" }}
          >
            <button
              type="button"
              onClick={() => setOpen(isOpen ? null : i)}
              className="flex w-full items-center justify-between gap-6 text-left"
              style={{ padding: "26px 0", color: "#002FA7" }}
              aria-expanded={isOpen}
              aria-controls={`faq-answer-${i}`}
            >
              <h3
                style={{
                  fontFamily: '"IBM Plex Sans", "IBM Plex Sans Fallback", sans-serif',
                  fontWeight: 300,
                  fontSize: "clamp(18px, 1.8vw, 24px)",
                  letterSpacing: "-0.01em",
                  lineHeight: 1.3,
                  margin: 0,
                }}
              >
                {it.q}
              </h3>
              <span
                className="mono"
                style={{
                  fontSize: 18,
                  color: "#8a6f3f",
                  flexShrink: 0,
                  transition: "transform .2s ease",
                  transform: isOpen ? "rotate(45deg)" : "rotate(0)",
                }}
              >
                +
              </span>
            </button>
            {/* Answers are always rendered into the DOM so the FAQPage schema
                matches the served HTML; closed items are hidden with CSS. */}
            <div
              id={`faq-answer-${i}`}
              hidden={!isOpen}
              style={{
                display: isOpen ? "block" : "none",
                paddingBottom: 28,
                color: "rgba(0,47,167,0.74)",
                fontSize: 15.5,
                lineHeight: 1.6,
                maxWidth: "68ch",
              }}
            >
              {it.answerContent ?? (it.aMd ? renderRichText(it.aMd) : it.a)}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
